import React, { useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import FormatUtils from '../../utils/FormatUtils.js';
import TextboxControl from '../Fields/TextboxControl';
import SelectListControl from '../Fields/SelectListControl';
import NumberControl from '../Fields/NumberControl';
import DecimalControl from '../Fields/DecimalControl';
import DateControl from '../Fields/DateControl';
import SwitchControl from '../Fields/SwitchControl';
import { ControlProps } from '../Fields/Types';
import { StringWithFormat } from '../../types/Result';
import { ITabelCellProps } from './Interfaces.js';
import UserDefinedFieldControl from '../Fields/UserDefinedFieldControl';
import { useAuth } from '../../contexts/authContext';
import CheckboxControl from '../Fields/CheckboxControl';
import WorkflowTaskModel from '../../models/WorkflowTaskModel.js';
import Button from '../Buttons/Button';
import PercentageControl from '../Fields/PercentageControl';
import WorkflowItemRevisionModel from '../../models/WorkflowItemRevisionModel';
import ItemColorControl from '../../views/Items/Components/ItemColorControl';

const TableCell: React.FC<ITabelCellProps> = ({ colInfo, colUserSettings, colIndex, rowIndex, refreshNr, rowData, serverErrors, onRowClick, onCellChange, onSelectedRowsChange }) => {
    const { isAuthorizedFor } = useAuth();
    const [cellRefreshNr, setCellRefreshNr] = useState(0);
    const formatUtils = new FormatUtils();


    if (colUserSettings === undefined || colUserSettings.visible === false || rowData === undefined)
        return null;

    let visible = colInfo.visible;
    if (visible && colInfo.ApplicationFunctions) {
        visible = false;
        colInfo.ApplicationFunctions.forEach((f) => {
            if (isAuthorizedFor(f))
                visible = true;
        });
    }

    if (visible === false || rowData === undefined) {
        return null;
    }

    let classnames = ''
    if (colInfo.align)
        classnames += ' text-' + colInfo.align;
    else if (colInfo.dataType === 'number' || colInfo.dataType === 'date' || colInfo.dataType === 'datetime' || colInfo.dataType === 'decimal' || colInfo.dataType === 'money' || colInfo.dataType === 'percentage')
        classnames += ' text-right';

    let style: React.CSSProperties = {};
    if (colInfo.getCellBackgroundColor)
        style.backgroundColor = colInfo.getCellBackgroundColor(rowData, colInfo);
    else if (colInfo.backgroundColor)
        style.backgroundColor = colInfo.backgroundColor;

    if (colInfo.getCellTextColor)
        style.color = colInfo.getCellTextColor(rowData, colInfo);
    else if (colInfo.textColor)
        style.color = colInfo.textColor;

    if (colInfo.maxWidth)
        style.maxWidth = colInfo.maxWidth;

    if (colInfo.minWidth)
        style.minWidth = colInfo.minWidth;

    if (colInfo.textOverflowEllipsis) {
        style.overflow = "hidden";
        style.textOverflow = "ellipsis";
        style.whiteSpace = "nowrap";
    }

    let value;
    if (colInfo.getCellData)
        value = colInfo.getCellData(rowData, colInfo);
    else if (colInfo.dataProp) {
        if (colInfo.dataProp === '.')
            value = rowData;
        else
            value = colInfo.dataProp.split('.').reduce((o, i) => (o === null || o === undefined) ? null : o[i], rowData);
    }

    let cellContent = value;

    let isButtonColumn = false;
    if (colInfo.headerButtons || colInfo.rowButtons || colInfo.checkboxButton) {
        isButtonColumn = true;
        classnames = 'button-column'
    }

    if (isButtonColumn && !colInfo.rowButtons && !colInfo.checkboxButton && !colInfo.cellRenderFunction)
        cellContent = null;

    if (colInfo.rowButtons)
        cellContent = colInfo.rowButtons.map((buttonInfo, index) => {
            let btnVisible = buttonInfo.isVisible
                ? buttonInfo.isVisible(rowData)
                : true;

            if (btnVisible && buttonInfo.applicationFunctions) {
                btnVisible = false;
                buttonInfo.applicationFunctions.forEach((f) => {
                    if (isAuthorizedFor(f))
                        btnVisible = true;
                });
            }

            if (btnVisible === true) {
                if (buttonInfo.icon) {
                    return (
                        <OverlayTrigger placement="top" overlay={(<Tooltip id="tooltip">{buttonInfo.text}</Tooltip>)} key={index}>
                            <span style={{ color: buttonInfo.color }} onClick={() => buttonInfo.onClick(rowData)}><i className={'fa ' + buttonInfo.icon} /></span>
                        </OverlayTrigger>
                    );
                }
                else
                    return <Button style={{ color: buttonInfo.color }} type={'button'} onClick={() => buttonInfo.onClick(rowData)} text={buttonInfo.text} />

            }
            return undefined;
        });

    function onCellClick(args: any) {
        if (colInfo.onCellClick)
            colInfo.onCellClick(rowData, colInfo)
        else
            onRowClick(args)
    }

    function onCellChanged(value: any) {
        if (onCellChange)
            onCellChange(value, rowData._rowIndex, colInfo.dataProp);

        if (colInfo.onCellChange)
            colInfo.onCellChange(value, rowData._rowIndex, colInfo.dataProp);
    }

    function onCellBlur() {
        if (colInfo.onCellBlur)
            colInfo.onCellBlur(rowData._rowIndex, colInfo.dataProp);
    }

    let validationResults: StringWithFormat[] = []
    if (serverErrors) {
        for (var i = 0; i < serverErrors.messages.length; i++) {
            var message = serverErrors.messages[i];
            for (var j = 0; j < message.memberNames.length; j++) {
                let fieldName = message.memberNames[j].charAt(0).toLowerCase() + message.memberNames[j].slice(1);
                if (fieldName === colInfo.dataProp) {
                    validationResults.push(message.message);
                }
            }
        }
    }

    let controlProps: ControlProps = {
        name: 'txt' + colInfo.dataProp + ' ' + rowIndex,
        value: cellContent,
        validationResults: validationResults,
        onChange: onCellChanged,
        onBlur: onCellBlur,
    }


    function changeChecked(value) {
        if (colInfo.checkboxButton) {
            rowData['_checkbox_' + colInfo.checkboxButton.text] = value;
            setCellRefreshNr(nr => nr + 1);
            onSelectedRowsChange(colInfo);
        }
    }

    function getButtons(rowData: WorkflowTaskModel) {
        const unhandableTaskTypes = [3, 4, 8, 10, 11, 12, 13, 14, 15]
        if (!unhandableTaskTypes.includes(rowData.taskDefinition.taskTypeId)){

            /*
            public WorkflowTaskAddBusinessPartnerModel WorkflowTaskAddBusinessPartnerModel { get; set; }
            public WorkflowTaskAddDocumentModel WorkflowTaskAddDocumentModel { get; set; }
            public WorkflowTaskAddPriceModel WorkflowTaskAddPriceModel { get; set; }
            public WorkflowTaskEditItemRevisionDatesModel WorkflowTaskEditItemRevisionDatesModel { get; set; }
            public WorkflowTaskEditItemModel WorkflowTaskEditItemModel { get; set; }
            public WorkflowTaskEditPricesModel WorkflowTaskEditPricesModel { get; set; }
            public WorkflowTaskReviseDocumentModel WorkflowTaskReviseDocumentModel { get; set; }
            public WorkflowTaskSetCustomFieldsModel WorkflowTaskSetCustomFieldsModel { get; set; }
            public WorkflowTaskSetProjectItemCustomFieldsModel WorkflowTaskSetProjectItemCustomFieldsModel { get; set; }
            */

            return rowData.nextWorkflowActions.map((action) => {
                return <Button text={action.name} onClick={() => NextAction(rowData, action.id)} />
            })
        }
        return <></>
    }

    function NextAction(rowData: WorkflowTaskModel, chosenWorkflowActionId?: number) {
        //console.log('Next step: ' + chosenWorkflowActionId);
        rowData.chosenWorkflowActionId = chosenWorkflowActionId;
        if (colInfo.onSubmitWorkflowTask)
            colInfo.onSubmitWorkflowTask(rowData)
    };

    if (colInfo.cellRenderFunction) {
        cellContent = colInfo.cellRenderFunction(rowData, colInfo)
    }
    else if (colInfo.checkboxButton) {
        controlProps.name = '_checkbox_' + colInfo.checkboxButton.text + ' ' + rowIndex;
        controlProps.value = rowData['_checkbox_' + colInfo.checkboxButton.text];
        controlProps.onChange = changeChecked;

        if (colInfo.checkboxButton.isCheckboxVisible === undefined || colInfo.checkboxButton.isCheckboxVisible(rowData)) {
            cellContent = <div style={{ margin: 'auto 12px' }}><CheckboxControl {...controlProps} /></div>
        }
        else {
            cellContent = ''
        }
    }
    else if (colInfo.dataType) {
        switch (colInfo.dataType) {
            case 'number':
                if (colInfo.editable) {
                    cellContent = <NumberControl {...controlProps} />
                } break;

            case 'date':
                if (colInfo.editable)
                    cellContent = <DateControl {...controlProps} />
                else if (value) {
                    cellContent = formatUtils.formatDate(value);
                } break;

            case 'datetime':
                if (value) {
                    cellContent = formatUtils.formatDateTime(value);
                } break;

            case 'decimal':
                if (colInfo.editable) {
                    cellContent = <DecimalControl {...controlProps} />
                } 
                else if (value) {
                    cellContent = formatUtils.formatDecimal(value, colInfo.decimals ? colInfo.decimals : 2);
                } break;

            case 'percentage':
                if (colInfo.editable) {
                    cellContent = <PercentageControl {...controlProps} />
                }
                else if (value) {
                    cellContent = formatUtils.formatPercentage(value);
                } break;

            case 'money':
                if (colInfo.editable) {
                    cellContent = <DecimalControl {...controlProps} />
                }
                else if (value) {
                    cellContent = formatUtils.formatMoney(value, colInfo.decimals ? colInfo.decimals : 2);
                } break;

            case 'bool':
                if (colInfo.editable) {
                    cellContent = <SwitchControl {...controlProps} />
                }
                else if (value !== undefined && value !== null) {
                    cellContent = formatUtils.formatBoolean(value, colInfo.trueValue ? colInfo.trueValue : 'Yes', colInfo.falseValue ? colInfo.falseValue : 'No');
                } break;

            case 'bigimage':
                if (value) {
                    if (colInfo.width)
                        cellContent = (<img height='96' width={colInfo.width} src={value} />);
                    else
                        cellContent = (<img height='96' src={value} />);
                } break;

            case 'image':
                if (value) {
                    cellContent = (<img height='32' src={value} />);
                } break;

            case 'thumbnail':
            case 'user':
                if (value?.thumbnailUrl) {
                    cellContent = (value &&
                        <div style={{ whiteSpace: 'nowrap' }}>
                            <div className='user' style={{ margin: -1 }}><div className='thumbnail'><img height='32' src={value.thumbnailUrl} /></div></div>
                            <div style={{ marginTop: 5, marginLeft: 3, float: 'left' }}>
                                {value.name}
                            </div>
                        </div>
                    );
                }
                else {
                    cellContent = (value &&
                        <div style={{ whiteSpace: 'nowrap' }}>
                            <div className='user' style={{ margin: -1 }}><div className='thumbnail' style={{ paddingTop: 3, textAlign: 'center', color: 'white', backgroundColor: value.color }}>{value.code}</div></div>
                            <div style={{ marginTop: 5, marginLeft: 3, float: 'left' }}>
                                {value.name} 
                            </div>
                        </div>
                    );
                }
                break;

            case 'project':
                cellContent = (value &&
                    <div style={{ whiteSpace: 'nowrap' }}>
                        <div className='user' style={{ margin: -1 }}><div className='thumbnail'><img height='32' src={value.thumbnailUrl} /></div></div>
                        <div style={{ marginTop: 5, marginLeft: 3, float: 'left' }}>
                            {value.id + ' - ' + value.name}
                        </div>
                    </div>
                );
                break;

            case 'brand':
                cellContent = (value &&
                    <div style={{ whiteSpace: 'nowrap' }}>
                        <div className='user' style={{ margin: -1 }}><div className='thumbnail'><img height='32' src={value.thumbnailUrl} /></div></div>
                        <div style={{ marginTop: 5, marginLeft: 3, float: 'left' }}>
                            {value.nameTree}
                        </div>
                    </div>
                );
                break;

            case 'item':
                cellContent = (value &&
                    <div style={{ whiteSpace: 'nowrap' }}>
                        <div className='user' style={{ margin: -1 }}><div className='thumbnail2'><img height='64' src={value.thumbnailUrl} /></div></div>
                        <div style={{ marginTop: 24, marginLeft: 3, float: 'left' }}>
                            {value.itemNr}
                        </div>
                    </div>
                );
                break;

            case 'itemrevision':
                cellContent = (value &&
                    <div style={{ whiteSpace: 'nowrap' }}>
                        <div className='user' style={{ margin: -1 }}><div className='thumbnail2'><img height='64' style={{ maxWidth: 64 }} src={value.thumbnailUrl} /></div></div>
                        <div style={{ marginTop: 20, marginLeft: 3, float: 'left' }}>
                            {value.item.itemNr + ' - v' + value.revisionNumber}
                        </div>
                    </div>
                );
                break;

            case 'workflowItemRevisions':
                if (value) {
                    cellContent = (value &&
                        <div>
                            {value.map(color => <ItemColorControl itemColor={color} onClick={() => { }} />)}
                        </div>
                    );
                }
                break;

            case 'userDefinedField':
                if (colInfo.getUserDefinedField) {
                    const udf = colInfo.getUserDefinedField(rowData, colInfo);
                    if (udf)
                        cellContent = <UserDefinedFieldControl controlProps={controlProps} dataTypeId={udf.dataTypeId} options={udf.options} />;
                }
                break;

            case 'workflowTask':
                controlProps.name = 'txtComments ' + rowIndex
                controlProps.value = rowData.comments
                controlProps.onChange = (val) => {
                    if (colInfo.onCellChange)
                        colInfo.onCellChange(val, rowData._rowIndex, 'comments')
                }

                cellContent =
                    <>
                        <TextboxControl {...controlProps} />
                        {getButtons(rowData)}
                    </>

                isButtonColumn = true
                break;
        }
    }
    else if (colInfo.editable) {
        if (colInfo.options)
            cellContent = <SelectListControl {...controlProps} options={colInfo.options} />
        else
            cellContent = <TextboxControl {...controlProps} />
    }

    if (!isButtonColumn && !colInfo.editable) {
        return (
            <td className={classnames} style={style} onClick={onCellClick} key={`${refreshNr}-${cellRefreshNr}-${rowData._rowIndex}-${colIndex}`}>
                {colInfo.tooltip ?
                    <OverlayTrigger placement="bottom-start" overlay={(<Tooltip id="tooltip">{cellContent}</Tooltip>)} key={rowIndex + cellContent}>
                        <span>{cellContent}</span>
                    </OverlayTrigger>
                    :
                    <span>{cellContent}</span>
                }
            </td>
        );
    }
    else
        return (
            <td className={classnames} style={style} key={`${refreshNr}-${cellRefreshNr}-${rowData._rowIndex}-${colIndex}`}>
                {colInfo.tooltip ?
                    <OverlayTrigger placement="bottom-start" overlay={(<Tooltip id="tooltip">{cellContent}</Tooltip>)} key={rowIndex + cellContent}>
                        <span>{cellContent}</span>
                    </OverlayTrigger>
                    :
                    <span>{cellContent}</span>
                }
            </td>
        );
}
export default TableCell;